import {http} from '../http'
import * as faceoffApi from '../../coreApi/faceoff'
import {
  FaceOffGoldenEdit,
  FaceOffGoldenSummary,
  FaceOffGoldenVersion,
  FaceOffRunDetail,
  FaceOffRunListEntry,
  FaceOffVerdictRequest,
} from '../../coreApi/faceoff'
import {APIResponse, APIResponseFailure} from '../types'
import {handleError} from '../handleError'

// handleError's message for a 404 when asked to flag it.
export const FACE_OFF_NOT_FOUND = 'notfound'
// The failure message for a 409 on a golden edit.
export const FACE_OFF_CONFLICT = 'conflict'

/**
 * Fills each re-baseline media left out: a run on another golden version from
 * the next older run. `runs` is newest first.
 */
export const fillRebaselines = (
  runs: FaceOffRunListEntry[],
): FaceOffRunListEntry[] =>
  runs.map((run, i) => {
    if (run.rebaseline !== null) return run
    const older = runs[i + 1]
    const rebaseline = !!older && older.goldenVersion !== run.goldenVersion
    return {
      ...run,
      rebaseline,
      baselineFrom: rebaseline ? older.goldenVersion : null,
    }
  })

/** Face Off runs, newest first. A malformed body resolves to a failure. */
const getFaceOffRuns = async (): Promise<
  APIResponse<FaceOffRunListEntry[]>
> => {
  try {
    const response = await faceoffApi.getFaceOffRuns(http)
    const {runs} = faceoffApi.decodeFaceOffRuns(response?.data)
    const sorted = [...runs].sort(
      (a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt),
    )
    return {result: 'success', data: fillRebaselines(sorted)}
  } catch (e) {
    return handleError(
      'api.faceoff.getFaceOffRuns()',
      'Face Off runs could not be loaded.',
      e,
    )
  }
}

/** One run's diff. A 404 fails with FACE_OFF_NOT_FOUND. */
const getFaceOffRun = async (
  runId: string,
): Promise<APIResponse<FaceOffRunDetail>> => {
  try {
    const response = await faceoffApi.getFaceOffRun(http, runId)
    return {
      result: 'success',
      data: faceoffApi.decodeFaceOffRun(response?.data),
    }
  } catch (e) {
    return handleError(
      'api.faceoff.getFaceOffRun()',
      'This Face Off run could not be loaded.',
      e,
      true,
    )
  }
}

const saveFaceOffVerdict = async (
  runId: string,
  body: FaceOffVerdictRequest,
): Promise<APIResponse<null>> => {
  try {
    await faceoffApi.postFaceOffVerdict(http, runId, body)
    return {result: 'success', data: null}
  } catch (e) {
    return handleError(
      'api.faceoff.saveFaceOffVerdict()',
      'The verdict could not be saved.',
      e,
    )
  }
}

/** Golden versions, newest first. */
const getFaceOffGoldenVersions = async (): Promise<
  APIResponse<FaceOffGoldenSummary[]>
> => {
  try {
    const response = await faceoffApi.getFaceOffGoldenVersions(http)
    const versions = faceoffApi.decodeGoldenVersions(response?.data)
    return {
      result: 'success',
      data: [...versions].sort((a, b) => b.version - a.version),
    }
  } catch (e) {
    return handleError(
      'api.faceoff.getFaceOffGoldenVersions()',
      'Golden versions could not be loaded.',
      e,
    )
  }
}

/** One golden version. A 404 fails with FACE_OFF_NOT_FOUND. */
const getFaceOffGoldenVersion = async (
  version: number,
): Promise<APIResponse<FaceOffGoldenVersion>> => {
  try {
    const response = await faceoffApi.getFaceOffGoldenVersion(http, version)
    return {
      result: 'success',
      data: faceoffApi.decodeGoldenVersion(response?.data),
    }
  } catch (e) {
    return handleError(
      'api.faceoff.getFaceOffGoldenVersion()',
      'This golden version could not be loaded.',
      e,
      true,
    )
  }
}

/** Removes a photo or a case. A 409 fails with FACE_OFF_CONFLICT. */
const editFaceOffGolden = async (
  edit: FaceOffGoldenEdit,
): Promise<APIResponse<FaceOffGoldenSummary>> => {
  const path = 'api.faceoff.editFaceOffGolden()'
  try {
    const response = await faceoffApi.postFaceOffGoldenEdit(http, edit)
    return {
      result: 'success',
      data: faceoffApi.decodeGoldenSummary(response?.data),
    }
  } catch (e) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((e as any)?.response?.status === 409) {
      const conflict: APIResponseFailure = {
        result: 'failure',
        error: {path, message: FACE_OFF_CONFLICT, cause: e},
      }
      return conflict
    }
    return handleError(path, 'The golden set could not be changed.', e)
  }
}

export const faceoff = {
  getFaceOffRuns,
  getFaceOffRun,
  saveFaceOffVerdict,
  getFaceOffGoldenVersions,
  getFaceOffGoldenVersion,
  editFaceOffGolden,
}
